/* eslint-disable @typescript-eslint/no-explicit-any */
import Input from "components/Input";
import Button from "components/Button";
import React, { useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import Select from "react-select";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";
import { setFeaturesEnabled } from "util/featuresUtil";

interface TrackingSettings {
  screenshotEnabled: boolean;
  deviceCheckInterval: number;
  dailyBreakLimit: number;
  sessionUploadInterval: number;
  urlSessionUploadInterval: number;
  deviceStatusUploadInterval: number;
  presenceCheckInterval: number;
  presenceUploadInterval: number;
  scheduleTrackingEnabled: boolean;
  idleThreshold: number;
  screenshotInterval: number;
  workStartTime: string;
  workEndTime: string;
  workDays: number[];
  dashboardEnabled?: boolean;
  screenshotQuality?: string;
  appTrackingEnabled?: boolean;
  urlTrackingEnabled?: boolean;
  presenceTrackingEnabled?: boolean;
  deviceTrackingEnabled?: boolean;
  wifiTrackingEnabled?: boolean;
  liveScreenEnabled?: boolean;
  liveScreenQuality?: string;
}

const DAYS_OF_WEEK = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 0, label: "Sunday" },
];

const QUALITY_OPTIONS = [
  {
    value: "low",
    label: "Low",
    resolution: "960 × 540",
    size: "5 fps",
    color: "text-green-600",
  },
  {
    value: "medium",
    label: "Medium",
    resolution: "1280 × 720",
    size: "10 fps",
    color: "text-orange-500",
  },
  {
    value: "high",
    label: "High",
    resolution: "1920 × 1080",
    size: "20 fps",
    color: "text-red-600",
  },
];

const formatQualityOption = (data: any, { context }: any) => {
  if (context === "value") {
    return (
      <div className="flex items-center text-sm font-medium">
        <svg
          className="w-4 h-4 mr-2 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="text-gray-900 dark:text-white">{data.label}</span>
        <span className="mx-2 text-gray-400">•</span>
        <span className="text-gray-500 dark:text-gray-400">{data.resolution}</span>
        <span className="mx-2 text-gray-400">•</span>
        <span className="text-gray-500 dark:text-gray-400">{data.size}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center w-1/3">
        <svg
          className={`w-5 h-5 mr-2 ${data.color}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="font-semibold text-gray-900 dark:text-white">
          {data.label}
        </span>
      </div>
      <div className="w-1/3 text-gray-600 dark:text-gray-400 flex items-center">
        <span className="mr-1.5 text-gray-400">•</span> {data.resolution}
      </div>
      <div className="w-1/3 text-gray-600 dark:text-gray-400 flex items-center">
        <span className="mr-1.5 text-gray-400">•</span> {data.size}
      </div>
    </div>
  );
};

interface UpdateSettingsProps {
  settings: TrackingSettings;
  onSuccess?: () => void;
  onCancel?: () => void;
  isModal?: boolean;
}

const UpdateSettings: React.FC<UpdateSettingsProps> = ({
  settings,
  onCancel,
  onSuccess,
  isModal = false,
}) => {
  const methods = useForm<TrackingSettings>({
    defaultValues: settings,
  });

  const [baselineSettings, setBaselineSettings] = useState<TrackingSettings>(settings);

  const [selectedDays, setSelectedDays] = useState(
    DAYS_OF_WEEK.filter((day) => settings.workDays.includes(day.value)),
  );

  const [screenshotEnabled, setScreenshotEnabled] = useState(
    settings.screenshotEnabled,
  );

  const [screenshotQuality, setScreenshotQuality] = useState(
    QUALITY_OPTIONS.find(
      (opt) => opt.value === (settings.screenshotQuality || "medium").toLowerCase()
    ) || QUALITY_OPTIONS[1]
  );

  const [liveScreenEnabled, setLiveScreenEnabled] = useState(
    settings.liveScreenEnabled ?? true,
  );

  const [liveScreenQuality, setLiveScreenQuality] = useState(
    QUALITY_OPTIONS.find(
      (opt) => opt.value === (settings.liveScreenQuality || "medium").toLowerCase()
    ) || QUALITY_OPTIONS[1]
  );

  const [scheduleTrackingEnabled, setScheduleTrackingEnabled] = useState(
    settings.scheduleTrackingEnabled,
  );
  const [dashboardEnabled, setDashboardEnabled] = useState(
    settings.dashboardEnabled ?? false,
  );
  const [appTrackingEnabled, setAppTrackingEnabled] = useState(
    settings.appTrackingEnabled ?? false,
  );
  const [urlTrackingEnabled, setUrlTrackingEnabled] = useState(
    settings.urlTrackingEnabled ?? true,
  );
  const [presenceTrackingEnabled, setPresenceTrackingEnabled] = useState(
    settings.presenceTrackingEnabled ?? false,
  );
  const [deviceTrackingEnabled, setDeviceTrackingEnabled] = useState(
    settings.deviceTrackingEnabled ?? true,
  );
  const [wifiTrackingEnabled, setWifiTrackingEnabled] = useState(
    settings.wifiTrackingEnabled ?? true,
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToaster();

  useEffect(() => {
    setBaselineSettings(settings);
    methods.reset(settings);
    setSelectedDays(
      DAYS_OF_WEEK.filter((day) => settings.workDays.includes(day.value)),
    );
    setScreenshotEnabled(settings.screenshotEnabled);
    setScreenshotQuality(
      QUALITY_OPTIONS.find(
        (opt) => opt.value === (settings.screenshotQuality || "medium").toLowerCase()
      ) || QUALITY_OPTIONS[1]
    );
    setLiveScreenEnabled(settings.liveScreenEnabled ?? true);
    setLiveScreenQuality(
      QUALITY_OPTIONS.find(
        (opt) => opt.value === (settings.liveScreenQuality || "medium").toLowerCase()
      ) || QUALITY_OPTIONS[1]
    );
    setScheduleTrackingEnabled(settings.scheduleTrackingEnabled);
    setDashboardEnabled(settings.dashboardEnabled ?? false);
    setAppTrackingEnabled(settings.appTrackingEnabled ?? false);
    setUrlTrackingEnabled(settings.urlTrackingEnabled ?? true);
    setPresenceTrackingEnabled(settings.presenceTrackingEnabled ?? false);
    setDeviceTrackingEnabled(settings.deviceTrackingEnabled ?? true);
    setWifiTrackingEnabled(settings.wifiTrackingEnabled ?? true);
  }, [settings, methods]);

  const onSubmit = async (data: TrackingSettings) => {
    try {
      setIsSubmitting(true);
      const formData = {
        ...data,
        workDays: selectedDays.map((day) => day.value),
        screenshotEnabled,
        screenshotQuality: screenshotQuality.value.toUpperCase(),
        liveScreenEnabled,
        liveScreenQuality: liveScreenQuality.value.toUpperCase(),
        scheduleTrackingEnabled,
        dashboardEnabled,
        appTrackingEnabled,
        urlTrackingEnabled,
        presenceTrackingEnabled,
        deviceTrackingEnabled,
        wifiTrackingEnabled,
      };

      const response = await apiService.post(
        "/api/tracking-global-settings/update",
        formData,
      );

      if (response.status === 200 || response.status === 201) {
        addToast("Device Settings Updates May Take Up to 1 Hour to Reflect!", "success");
        setFeaturesEnabled({
          screenshotEnabled: formData.screenshotEnabled,
          appTrackingEnabled: formData.appTrackingEnabled,
          urlTrackingEnabled: formData.urlTrackingEnabled,
          presenceTrackingEnabled: formData.presenceTrackingEnabled,
          deviceTrackingEnabled: formData.deviceTrackingEnabled,
          liveScreenEnabled: formData.liveScreenEnabled,
        });
        setBaselineSettings(formData);
        methods.reset(formData);

        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error: any) {
      console.error("Error updating tracking settings:", error);
      addToast(
        error?.response?.data?.message || "Failed to update tracking settings",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const { isDirty } = methods.formState;
  const currentWorkDays = selectedDays.map((day) => day.value).sort().join(",");
  const initialWorkDays = [...baselineSettings.workDays].sort().join(",");
  const isWorkDaysModified = currentWorkDays !== initialWorkDays;

  const isModified =
    isDirty ||
    screenshotEnabled !== baselineSettings.screenshotEnabled ||
    screenshotQuality.value !== (baselineSettings.screenshotQuality || "medium").toLowerCase() ||
    liveScreenEnabled !== (baselineSettings.liveScreenEnabled ?? true) ||
    liveScreenQuality.value !== (baselineSettings.liveScreenQuality || "medium").toLowerCase() ||
    scheduleTrackingEnabled !== baselineSettings.scheduleTrackingEnabled ||
    dashboardEnabled !== (baselineSettings.dashboardEnabled ?? false) ||
    appTrackingEnabled !== (baselineSettings.appTrackingEnabled ?? false) ||
    urlTrackingEnabled !== (baselineSettings.urlTrackingEnabled ?? true) ||
    presenceTrackingEnabled !== (baselineSettings.presenceTrackingEnabled ?? false) ||
    deviceTrackingEnabled !== (baselineSettings.deviceTrackingEnabled ?? true) ||
    wifiTrackingEnabled !== (baselineSettings.wifiTrackingEnabled ?? true) ||
    isWorkDaysModified;

  return (
    <div className={isModal ? "" : "py-4 px-4"}>
      <div className="max-w-7xl mx-auto relative">
        <div className={isModal ? "" : "rounded-lg p-4"}>

          {/* Header Section (Conditionally sticky based on isModal) */}
          <div
            className={`sticky z-30 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ${isModal
                ? "top-0 pt-0 pb-4"
                : "top-[56px] pt-2 pb-6 before:content-[''] before:absolute before:-top-[56px] before:left-0 before:right-0 before:h-[56px] before:bg-white dark:before:bg-gray-800"
              }`}
          >
            <div className="relative z-10">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Update Tracking Settings
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Modify your employee tracking and monitoring preferences
              </p>
            </div>
          </div>

          <div className={isModal ? "mt-4" : "mt-6"}>
            <FormProvider {...methods}>
              <form
                onSubmit={methods.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                {/* Screenshot Settings Section */}
                <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                    <svg
                      className="w-6 h-6 mr-2 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Screenshot Settings
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    <div className="flex items-center pt-3 pb-3 md:pb-0">
                      <input
                        type="checkbox"
                        id="screenshotEnabled"
                        checked={screenshotEnabled}
                        onChange={(e) => setScreenshotEnabled(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        htmlFor="screenshotEnabled"
                        className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Enable Screenshots
                      </label>
                    </div>
                    <Input
                      name="screenshotInterval"
                      label="Screenshot Interval (seconds)"
                      type="number"
                      placeholder="600"
                      min={60}
                    />

                    {/* Fixed dropdown width to match standard inputs */}
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        Screenshot Quality
                      </label>
                      <Select
                        value={screenshotQuality}
                        onChange={(selected) => setScreenshotQuality(selected as any)}
                        options={QUALITY_OPTIONS}
                        formatOptionLabel={formatQualityOption}
                        classNamePrefix="select"
                        placeholder="Select quality..."
                        unstyled
                        classNames={{
                          control: (state) =>
                            `min-h-[42px] border rounded-lg px-2 ${state.isFocused
                              ? "border-blue-500 ring-1 ring-blue-500"
                              : "border-gray-300 dark:border-gray-600"
                            } bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200`,
                          menu: () =>
                            "mt-1 rounded-lg shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 z-50 overflow-hidden",
                          menuList: () => "py-0",
                          option: (state) =>
                            `px-3 py-2 cursor-pointer border-b border-gray-100 dark:border-gray-700/50 last:border-0 ${state.isFocused
                              ? "bg-gray-50 dark:bg-gray-700/50"
                              : "bg-white dark:bg-gray-800"
                            }`,
                          singleValue: () => "text-gray-900 dark:text-white",
                          input: () => "text-gray-900 dark:text-white m-0",
                          placeholder: () => "text-gray-500 dark:text-gray-400 mx-0.5",
                          clearIndicator: () => "p-1 cursor-pointer",
                          dropdownIndicator: () => "p-1 cursor-pointer",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Live Screen Settings Section */}
                <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                    <svg
                      className="w-6 h-6 mr-2 text-pink-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Live Screen Settings
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    <div className="flex items-center pt-3 pb-3 md:pb-0 md:pt-[34px]">
                      <input
                        type="checkbox"
                        id="liveScreenEnabled"
                        checked={liveScreenEnabled}
                        onChange={(e) => setLiveScreenEnabled(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        htmlFor="liveScreenEnabled"
                        className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Enable Live Screen
                      </label>
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        Live Screen Quality
                      </label>
                      <Select
                        value={liveScreenQuality}
                        onChange={(selected) => setLiveScreenQuality(selected as any)}
                        options={QUALITY_OPTIONS}
                        formatOptionLabel={formatQualityOption}
                        classNamePrefix="select"
                        placeholder="Select quality..."
                        unstyled
                        classNames={{
                          control: (state) =>
                            `min-h-[42px] border rounded-lg px-2 ${state.isFocused
                              ? "border-blue-500 ring-1 ring-blue-500"
                              : "border-gray-300 dark:border-gray-600"
                            } bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200`,
                          menu: () =>
                            "mt-1 rounded-lg shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 z-50 overflow-hidden",
                          menuList: () => "py-0",
                          option: (state) =>
                            `px-3 py-2 cursor-pointer border-b border-gray-100 dark:border-gray-700/50 last:border-0 ${state.isFocused
                              ? "bg-gray-50 dark:bg-gray-700/50"
                              : "bg-white dark:bg-gray-800"
                            }`,
                          singleValue: () => "text-gray-900 dark:text-white",
                          input: () => "text-gray-900 dark:text-white m-0",
                          placeholder: () => "text-gray-500 dark:text-gray-400 mx-0.5",
                          clearIndicator: () => "p-1 cursor-pointer",
                          dropdownIndicator: () => "p-1 cursor-pointer",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Time Intervals Section */}
                <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                    <svg
                      className="w-6 h-6 mr-2 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Time Intervals
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Input
                      name="deviceCheckInterval"
                      label="Device Check Interval (seconds)"
                      type="number"
                      placeholder="10800"
                      min={60}
                    />
                    <Input
                      name="sessionUploadInterval"
                      label="Session Upload Interval (seconds)"
                      type="number"
                      placeholder="3600"
                      min={60}
                    />
                    <Input
                      name="urlSessionUploadInterval"
                      label="URL Session Upload Interval (seconds)"
                      type="number"
                      placeholder="3600"
                      min={60}
                    />
                    <Input
                      name="deviceStatusUploadInterval"
                      label="Device Status Upload Interval (seconds)"
                      type="number"
                      placeholder="600"
                      min={60}
                    />
                    <Input
                      name="presenceCheckInterval"
                      label="Presence Check Interval (seconds)"
                      type="number"
                      placeholder="600"
                      min={60}
                    />
                    <Input
                      name="presenceUploadInterval"
                      label="Presence Upload Interval (seconds)"
                      type="number"
                      placeholder="3600"
                      min={60}
                    />
                  </div>
                </div>

                {/* Work Schedule Section */}
                <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                    <svg
                      className="w-6 h-6 mr-2 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    Work Schedule
                  </h2>
                  <div className="space-y-6">
                    <div className="flex items-center mb-4">
                      <input
                        type="checkbox"
                        id="scheduleTrackingEnabled"
                        checked={scheduleTrackingEnabled}
                        onChange={(e) =>
                          setScheduleTrackingEnabled(e.target.checked)
                        }
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        htmlFor="scheduleTrackingEnabled"
                        className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Enable Schedule Tracking
                      </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        name="workStartTime"
                        label="Work Start Time"
                        type="time"
                        placeholder="10:00"
                      />
                      <Input
                        name="workEndTime"
                        label="Work End Time"
                        type="time"
                        placeholder="18:00"
                      />
                    </div>

                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        Work Days <span className="text-red-500">*</span>
                      </label>
                      <Select
                        isMulti
                        value={selectedDays}
                        onChange={(selected) => setSelectedDays(selected as any)}
                        options={DAYS_OF_WEEK}
                        classNamePrefix="select"
                        placeholder="Select work days..."
                        unstyled
                        classNames={{
                          control: (state) =>
                            `min-h-[42px] border rounded-lg px-2 ${state.isFocused
                              ? "border-blue-500 ring-1 ring-blue-500"
                              : "border-gray-300 dark:border-gray-600"
                            } bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200`,
                          menu: () =>
                            "mt-1 rounded-lg shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700",
                          menuList: () => "py-1",
                          option: (state) =>
                            `px-3 py-2 cursor-pointer ${state.isFocused
                              ? "bg-gray-100 dark:bg-gray-700"
                              : "bg-white dark:bg-gray-800"
                            } text-gray-900 dark:text-gray-200`,
                          multiValue: () => "bg-blue-500 rounded flex m-1",
                          multiValueLabel: () => "text-white px-2 py-0.5 text-sm",
                          multiValueRemove: () =>
                            "text-white hover:bg-blue-600 hover:text-white px-1 rounded-r cursor-pointer transition-colors",
                          input: () => "text-gray-900 dark:text-white m-0.5",
                          placeholder: () =>
                            "text-gray-500 dark:text-gray-400 mx-0.5",
                          clearIndicator: () =>
                            "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer p-1",
                          dropdownIndicator: () =>
                            "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer p-1",
                        }}
                      />
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Select the days of the week when tracking should be active
                      </p>
                    </div>
                  </div>
                </div>

                {/* Behavior Settings Section */}
                <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                    <svg
                      className="w-6 h-6 mr-2 text-orange-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Behaviour Settings
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      name="idleThreshold"
                      label="Idle Threshold (seconds)"
                      type="number"
                      placeholder="130"
                      min={30}
                    />
                    <Input
                      name="dailyBreakLimit"
                      label="Daily Break Limit (seconds)"
                      type="number"
                      placeholder="5400"
                      min={0}
                    />
                  </div>
                </div>

                {/* User Dashboard Settings Section */}
                <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                    <svg
                      className="w-6 h-6 mr-2 text-teal-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                      />
                    </svg>
                    User Dashboard Settings
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="dashboardEnabled"
                        checked={dashboardEnabled}
                        onChange={(e) => setDashboardEnabled(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        htmlFor="dashboardEnabled"
                        className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Enable User Dashboard
                      </label>
                    </div>
                  </div>
                </div>

                {/* Feature Control Section */}
                <div className="pb-6">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                    <svg
                      className="w-6 h-6 mr-2 text-indigo-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                      />
                    </svg>
                    Feature Control
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="appTrackingEnabled"
                        checked={appTrackingEnabled}
                        onChange={(e) => setAppTrackingEnabled(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        htmlFor="appTrackingEnabled"
                        className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Enable App Tracking
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="urlTrackingEnabled"
                        checked={urlTrackingEnabled}
                        onChange={(e) => setUrlTrackingEnabled(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        htmlFor="urlTrackingEnabled"
                        className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Enable URL Tracking
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="presenceTrackingEnabled"
                        checked={presenceTrackingEnabled}
                        onChange={(e) => setPresenceTrackingEnabled(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        htmlFor="presenceTrackingEnabled"
                        className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Face Detection
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="deviceTrackingEnabled"
                        checked={deviceTrackingEnabled}
                        onChange={(e) => setDeviceTrackingEnabled(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        htmlFor="deviceTrackingEnabled"
                        className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Enable Device Tracking
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="wifiTrackingEnabled"
                        checked={wifiTrackingEnabled}
                        onChange={(e) => setWifiTrackingEnabled(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        htmlFor="wifiTrackingEnabled"
                        className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                      >
                        Enable WiFi Tracking
                      </label>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {/* Action Buttons */}
                <div className="flex space-x-4">
                  <Button
                    type="button"
                    color="gray"
                    onClick={onCancel}
                    disabled={isSubmitting}
                    className="bg-gray-500 hover:bg-gray-600"
                  >
                    <svg
                      className="w-4 h-4 mr-2 inline"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    color="gray"
                    onClick={() => {
                      // 1. Check if there are any changes first
                      if (!isModified) {
                        addToast("No change in settings", "info"); // You can use "info", "success", or "error" based on your toaster config
                        return;
                      }

                      // 2. Perform the reset if modified
                      methods.reset(baselineSettings);
                      setSelectedDays(
                        DAYS_OF_WEEK.filter((day) =>
                          baselineSettings.workDays.includes(day.value)
                        )
                      );
                      setScreenshotEnabled(baselineSettings.screenshotEnabled);
                      setScreenshotQuality(
                        QUALITY_OPTIONS.find(
                          (opt) => opt.value === (baselineSettings.screenshotQuality || "medium").toLowerCase()
                        ) || QUALITY_OPTIONS[1]
                      );
                      setLiveScreenEnabled(baselineSettings.liveScreenEnabled ?? true);
                      setLiveScreenQuality(
                        QUALITY_OPTIONS.find(
                          (opt) => opt.value === (baselineSettings.liveScreenQuality || "medium").toLowerCase()
                        ) || QUALITY_OPTIONS[1]
                      );
                      setScheduleTrackingEnabled(
                        baselineSettings.scheduleTrackingEnabled
                      );
                      setDashboardEnabled(baselineSettings.dashboardEnabled ?? false);
                      setAppTrackingEnabled(baselineSettings.appTrackingEnabled ?? false);
                      setUrlTrackingEnabled(baselineSettings.urlTrackingEnabled ?? true);
                      setPresenceTrackingEnabled(baselineSettings.presenceTrackingEnabled ?? false);
                      setDeviceTrackingEnabled(baselineSettings.deviceTrackingEnabled ?? true);
                      setWifiTrackingEnabled(baselineSettings.wifiTrackingEnabled ?? true);

                      // 3. Show success toast after resetting
                      addToast("Tracking settings resetted successfully", "success");
                    }}
                    // Remove !isModified so the button is always clickable
                    disabled={isSubmitting}
                    className="bg-gray-400 hover:bg-gray-500"
                  >
                    <svg
                      className="w-4 h-4 mr-2 inline"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    color="blue"
                    disabled={isSubmitting || !isModified}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Updating...
                      </span>
                    ) : (
                      "Update Settings"
                    )}
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateSettings;